import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { setGrowlListener, showGrowl, type GrowlVariant } from './growlState';

const DISMISS_MS: Record<GrowlVariant, number> = {
  success: 3800,
  error: 6000,
  info: 4500,
};

type Item = { id: string; message: string; variant: GrowlVariant };

type GrowlCtx = {
  show: (message: string, variant?: GrowlVariant) => void;
};

const Ctx = createContext<GrowlCtx | null>(null);

export function useGrowl(): GrowlCtx {
  const c = useContext(Ctx);
  if (!c) {
    return {
      show: (message: string, variant: GrowlVariant = 'info') => showGrowl(message, variant),
    };
  }
  return c;
}

export function GrowlProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Item[]>([]);

  const push = useCallback((item: Item) => {
    setItems((prev) => [...prev.slice(-5), item]);
    const ms = DISMISS_MS[item.variant] ?? 4000;
    window.setTimeout(() => {
      setItems((s) => s.filter((x) => x.id !== item.id));
    }, ms);
  }, []);

  useEffect(() => {
    setGrowlListener((item) => push(item));
    return () => setGrowlListener(null);
  }, [push]);

  const ctx = useMemo(
    () => ({
      show: (message: string, variant: GrowlVariant = 'info') => showGrowl(message, variant),
    }),
    []
  );

  return (
    <Ctx.Provider value={ctx}>
      {children}
      <div
        role="status"
        aria-live="polite"
        style={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          alignItems: 'flex-end',
          pointerEvents: 'none',
          maxWidth: 'min(400px, calc(100vw - 32px))',
        }}
      >
        {items.map((g) => {
          const colors =
            g.variant === 'success'
              ? { bg: '#ecfdf5', border: '#6ee7b7', text: '#064e3b' }
              : g.variant === 'error'
                ? { bg: '#fef2f2', border: '#fecaca', text: '#991b1b' }
                : { bg: '#f8fafc', border: '#cbd5e1', text: '#0f172a' };
          return (
            <div
              key={g.id}
              className="va-growl-toast"
              style={{
                pointerEvents: 'auto',
                background: colors.bg,
                color: colors.text,
                border: `1px solid ${colors.border}`,
                borderRadius: 10,
                padding: '12px 16px',
                fontSize: 14,
                lineHeight: 1.4,
                boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.04)',
                maxWidth: '100%',
                animation: 'vaGrowlIn 0.28s ease-out',
              }}
            >
              {g.message}
            </div>
          );
        })}
      </div>
    </Ctx.Provider>
  );
}
